// src/pages/ProductPage.js
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  MessageSquare, Users, Shield, BarChart3, Zap,
  CheckCircle, Accessibility, FileText, Headphones,
  Languages, Library, RefreshCw, Palette, ArrowRight
} from 'lucide-react';
import { pageVariants, pageTransition } from '../utils/pageTransitions';

const ProductPage = () => {
  const navigate = useNavigate();
  const [animationStep, setAnimationStep] = useState(0);
  const [activeIcons, setActiveIcons] = useState([]);

  // Animation sequence steps
  useEffect(() => {
    const animationSequence = [
      // R -> CB (gray)
      { step: 0, activeIcons: ['resident', 'chatbot'], delay: 1200 },
      // CB -> KB (gray)
      { step: 1, activeIcons: ['chatbot', 'policy'], delay: 1200 },
      // KB -> CB (green)
      { step: 2, activeIcons: ['policy', 'chatbot'], delay: 1200 },
      // CB -> R (green)
      { step: 3, activeIcons: ['chatbot', 'resident'], delay: 1200 },
      // Pause
      { step: 4, activeIcons: [], delay: 800 },
      // R -> CB (gray)
      { step: 5, activeIcons: ['resident', 'chatbot'], delay: 1200 },
      // CB -> KB (gray)
      { step: 6, activeIcons: ['chatbot', 'policy'], delay: 1200 },
      // KB -> CB (red - failure)
      { step: 7, activeIcons: ['policy', 'chatbot'], delay: 1200 },
      // CB -> R (red - reports failure)
      { step: 8, activeIcons: ['chatbot', 'resident'], delay: 1200 },
      // R -> HAS (gray - resident contacts staff)
      { step: 9, activeIcons: ['resident', 'staff'], delay: 1200 },
      // HAS -> R (green - staff responds with success)
      { step: 10, activeIcons: ['staff', 'resident'], delay: 1200 },
      // Pause before loop
      { step: 11, activeIcons: [], delay: 1000 },
    ];

    const currentSequence = animationSequence[animationStep];
    setActiveIcons(currentSequence.activeIcons);

    const timer = setTimeout(() => {
      setAnimationStep((prev) => (prev + 1) % animationSequence.length);
    }, currentSequence.delay);

    return () => clearTimeout(timer);
  }, [animationStep]);

  // Icon positions (as percentages)
  const iconPositions = {
    resident: { x: 0, y: 35 },
    chatbot: { x: 45, y: 35 },
    policy: { x: 80, y: 10 },
    staff: { x: 80, y: 60 }
  };

  // Determine particle properties based on step
  const getParticleProps = (from, to, step) => {
    const particles = {
      'resident-chatbot': { show: [0, 5], color: 'gray' },
      'chatbot-policy': { show: [1, 6], color: 'gray' },
      'policy-chatbot': { show: [2], color: 'green', failure: [7] },
      'chatbot-resident': { show: [3], color: 'green', failure: [8] },
      'resident-staff': { show: [9], color: 'gray' },
      'staff-resident': { show: [10], color: 'green' },
    };

    const particleConfig = particles[`${from}-${to}`];
    if (!particleConfig) return null;

    if (particleConfig.show.includes(step)) {
      return { color: particleConfig.color };
    }
    if (particleConfig.failure?.includes(step)) {
      return { color: 'red' };
    }
    return null;
  };

  const Particle = ({ from, to, step }) => {
    const props = getParticleProps(from, to, step);
    if (!props) return null;

    const colorClasses = {
      gray: 'bg-gray-400',
      green: 'bg-green-500',
      red: 'bg-red-500'
    };

    // Determine if this is a forward or return leg
    const isForwardLeg = (from === 'resident' && (to === 'chatbot' || to === 'staff')) ||
      (from === 'chatbot' && (to === 'policy'));

    // Special case: staff to resident should go 9 o'clock to 3 o'clock
    const isStaffToResident = (from === 'staff' && to === 'resident');

    // Check if this is a resident-staff connection (needs curved path)
    const isResidentStaffConnection = (from === 'resident' && to === 'staff') || (from === 'staff' && to === 'resident');

    // Icon radius and positioning adjustments based on manual testing
    const iconRadius = 11; // Percentage of container width for 3 o'clock positions
    const leftEdgeOffset = 1.6; // Percentage offset for 9 o'clock positions (found through testing)
    const centerYOffset = 9; // Y offset to account for icon centering vs particle positioning
    const particleSize = 12; // Main particle size in pixels

    // Convert particle size to percentage (rough estimate for typical container)
    const particleSizePercent = 0.5;

    // Calculate edge positions
    const getEdgePosition = (icon, isStartPosition, isForward) => {
      const basePos = iconPositions[icon];

      // Adjust for actual visual center of icons
      const visualCenter = {
        x: basePos.x,
        y: basePos.y + centerYOffset
      };

      // Special case for staff to resident
      if (isStaffToResident) {
        if (isStartPosition) {
          // Start at 9 o'clock of staff (left edge)
          return {
            x: visualCenter.x - leftEdgeOffset - particleSizePercent / 2,
            y: visualCenter.y - particleSizePercent / 2
          };
        } else {
          // End at 3 o'clock of resident (right edge)
          return {
            x: visualCenter.x + iconRadius - particleSizePercent / 2,
            y: visualCenter.y - particleSizePercent / 2
          };
        }
      }

      if (isStartPosition) {
        // Starting position
        if (isForward) {
          // Forward leg: start at 3 o'clock (right edge)
          return {
            x: visualCenter.x + iconRadius - particleSizePercent / 2,
            y: visualCenter.y - particleSizePercent / 2
          };
        } else {
          // Return leg: start at 9 o'clock (left edge)
          return {
            x: visualCenter.x - leftEdgeOffset - particleSizePercent / 2,
            y: visualCenter.y - particleSizePercent / 2
          };
        }
      } else {
        // Destination position
        if (isForward) {
          // Forward leg: end at 9 o'clock (left edge)
          return {
            x: visualCenter.x - leftEdgeOffset - particleSizePercent / 2,
            y: visualCenter.y - particleSizePercent / 2
          };
        } else {
          // Return leg: end at 3 o'clock (right edge)
          return {
            x: visualCenter.x + iconRadius - particleSizePercent / 2,
            y: visualCenter.y - particleSizePercent / 2
          };
        }
      }
    };

    const fromPos = getEdgePosition(from, true, isForwardLeg);
    const toPos = getEdgePosition(to, false, isForwardLeg);

    // Create curved path for resident-staff connections
    let animationProps;
    if (isResidentStaffConnection) {
      // Calculate midpoint for downward curve (using your manual adjustment)
      const midX = (fromPos.x + toPos.x + 11) / 2;
      const midY = Math.max(fromPos.y, toPos.y) + 15; // 15% below the lower point

      // Use custom motion with bezier curve calculation
      animationProps = {
        // Custom property that we'll use to calculate position
        progress: 1,
        opacity: [0, 1, 1, 0.8],
        scale: [0.5, 1.2, 1, 0.8]
      };
    } else {
      // Straight path for other connections
      animationProps = {
        left: `${toPos.x}%`,
        top: `${toPos.y}%`,
        opacity: [0, 1, 1, 0.8],
        scale: [0.5, 1.2, 1, 0.8]
      };
    }

    // For curved paths, create custom animated particles
    if (isResidentStaffConnection) {
      const midX = (fromPos.x + toPos.x + 11) / 2;
      const midY = Math.max(fromPos.y, toPos.y) + 15;

      const trailCount = 4;
      const particles = [];

      // Create smooth parabolic path using quadratic bezier formula
      const createParabolicPath = () => {
        const steps = 20; // More steps for smoother curve
        const xPath = [];
        const yPath = [];

        for (let i = 0; i <= steps; i++) {
          const t = i / steps;
          // Quadratic Bezier formula: B(t) = (1-t)²P₀ + 2(1-t)tP₁ + t²P₂
          const x = Math.pow(1 - t, 2) * fromPos.x + 2 * (1 - t) * t * midX + Math.pow(t, 2) * toPos.x;
          const y = Math.pow(1 - t, 2) * fromPos.y + 2 * (1 - t) * t * midY + Math.pow(t, 2) * toPos.y;
          xPath.push(x + '%');
          yPath.push(y + '%');
        }
        return { xPath, yPath };
      };

      const { xPath, yPath } = createParabolicPath();

      for (let i = 0; i <= trailCount; i++) {
        const isMain = i === 0;
        const delay = i * 0.08;
        const opacity = isMain ? 1 : Math.max(0.2, 1 - (i * 0.25));
        const scale = isMain ? 1 : Math.max(0.4, 1 - (i * 0.15));

        particles.push(
          <motion.div
            key={`${from}-${to}-${step}-${i}`}
            className={`absolute rounded-full ${colorClasses[props.color]} shadow-lg z-20`}
            style={{
              width: isMain ? '12px' : '8px',
              height: isMain ? '12px' : '8px',
            }}
            initial={{
              left: `${fromPos.x}%`,
              top: `${fromPos.y}%`,
              opacity: 0,
              scale: 0.5
            }}
            animate={{
              left: xPath,
              top: yPath,
              opacity: [0, opacity, opacity, opacity * 0.8],
              scale: [0.5, scale * 1.2, scale, scale * 0.8]
            }}
            exit={{
              opacity: 0,
              scale: 0.3
            }}
            transition={{
              duration: 1,
              delay: delay,
              ease: "linear",
              opacity: {
                times: [0, 0.1, 0.9, 1],
                duration: 1
              },
              scale: {
                times: [0, 0.1, 0.3, 1],
                duration: 1
              }
            }}
          />
        );
      }

      return (
        <AnimatePresence>
          {particles}
        </AnimatePresence>
      );
    }

    // Create trail particles (main particle + 4 trail particles)
    const trailCount = 4;
    const particles = [];

    for (let i = 0; i <= trailCount; i++) {
      const isMain = i === 0;
      const delay = i * 0.08; // 80ms delay between trail particles
      const opacity = isMain ? 1 : Math.max(0.2, 1 - (i * 0.25));
      const scale = isMain ? 1 : Math.max(0.4, 1 - (i * 0.15));

      particles.push(
        <motion.div
          key={`${from}-${to}-${step}-${i}`}
          className={`absolute rounded-full ${colorClasses[props.color]} shadow-lg z-20`}
          style={{
            width: isMain ? '12px' : '8px',
            height: isMain ? '12px' : '8px',
          }}
          initial={{
            left: `${fromPos.x}%`,
            top: `${fromPos.y}%`,
            opacity: 0,
            scale: 0.5
          }}
          animate={{
            ...animationProps,
            opacity: [0, opacity, opacity, opacity * 0.8],
            scale: [0.5, scale * 1.2, scale, scale * 0.8]
          }}
          exit={{
            opacity: 0,
            scale: 0.3
          }}
          transition={{
            duration: 1,
            delay: delay,
            ease: "easeInOut",
            opacity: {
              times: [0, 0.1, 0.9, 1],
              duration: 1
            },
            scale: {
              times: [0, 0.1, 0.3, 1],
              duration: 1
            }
          }}
        />
      );
    }

    return (
      <AnimatePresence>
        {particles}
      </AnimatePresence>
    );
  };

  return (
    <motion.div
      initial="initial"
      animate="in"
      exit="out"
      variants={pageVariants}
      transition={pageTransition}
      className="min-h-screen pt-20"
    >
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="text-center mb-16"
            >
              <h1 className="text-4xl font-bold text-gray-900 mb-4">
                Intelligent Support for Everyone
              </h1>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Our AI-powered chat widget provides instant, accurate answers to residents, applicants,
                and the public while freeing your staff to focus on complex cases.
              </p>
            </motion.div>

            {/* How It Works Diagram */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-3xl shadow-xl p-8 mb-16 overflow-hidden"
            >
              <h3 className="text-2xl font-bold mb-8 text-center">How It Works</h3>
              <div className="relative h-96 max-w-3xl mx-auto">
                {/* Resident */}
                <motion.div
                  className="absolute text-center z-10"
                  style={{
                    left: `${iconPositions.resident.x}%`,
                    top: `${iconPositions.resident.y}%`,
                    transform: 'translate(-50%, -50%)'
                  }}
                  animate={{
                    scale: activeIcons.includes('resident') ? 1.1 : 1,
                  }}
                  transition={{ duration: 0.3 }}
                >
                  <motion.div
                    className={`w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mb-3 mx-auto ${activeIcons.includes('resident') ? 'ring-4 ring-blue-300' : ''
                      }`}
                  >
                    <Users className="h-10 w-10 text-blue-600" />
                  </motion.div>
                  <p className="font-semibold">Resident</p>
                </motion.div>

                {/* AI Chatbot */}
                <motion.div
                  className="absolute text-center z-10"
                  style={{
                    left: `${iconPositions.chatbot.x}%`,
                    top: `${iconPositions.chatbot.y}%`,
                    transform: 'translate(-50%, -50%)'
                  }}
                  animate={{
                    scale: activeIcons.includes('chatbot') ? 1.1 : 1,
                  }}
                  transition={{ duration: 0.3 }}
                >
                  <motion.div
                    className={`w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mb-3 mx-auto ${activeIcons.includes('chatbot') ? 'ring-4 ring-purple-300' : ''
                      }`}
                  >
                    <MessageSquare className="h-10 w-10 text-purple-600" />
                  </motion.div>
                  <p className="font-semibold">AI Chatbot</p>
                </motion.div>

                {/* HA Policy */}
                <motion.div
                  className="absolute text-center z-10"
                  style={{
                    left: `${iconPositions.policy.x}%`,
                    top: `${iconPositions.policy.y}%`,
                    transform: 'translate(-50%, -50%)'
                  }}
                  animate={{
                    scale: activeIcons.includes('policy') ? 1.1 : 1,
                    x: [7, 8, 9, 10].includes(animationStep) && activeIcons.includes('policy') ? [0, -2, 2, -1, 1, 0] : 0,
                  }}
                  transition={{
                    duration: 0.3,
                    x: { duration: 0.5, repeat: [7, 8, 9, 10].includes(animationStep) && activeIcons.includes('policy') ? Infinity : 0, repeatType: "reverse" }
                  }}
                >
                  <motion.div
                    className={`w-20 h-20 rounded-full flex items-center justify-center mb-3 mx-auto ${[7, 8, 9, 10].includes(animationStep)
                      ? 'bg-red-100 ring-4 ring-red-300'
                      : activeIcons.includes('policy')
                        ? 'bg-green-100 ring-4 ring-green-300'
                        : 'bg-green-100'
                      }`}
                  >
                    <FileText className={`h-10 w-10 ${[7, 8, 9, 10].includes(animationStep) ? 'text-red-600' : 'text-green-600'
                      }`} />
                  </motion.div>
                  <p className="font-semibold">HA Policy</p>
                </motion.div>

                {/* HA Staff */}
                <motion.div
                  className="absolute text-center z-10"
                  style={{
                    left: `${iconPositions.staff.x}%`,
                    top: `${iconPositions.staff.y}%`,
                    transform: 'translate(-50%, -50%)'
                  }}
                  animate={{
                    scale: activeIcons.includes('staff') ? 1.1 : 1,
                    opacity: activeIcons.includes('staff') ? 1 : 0.75,
                  }}
                  transition={{ duration: 0.3 }}
                >
                  <motion.div
                    className={`w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mb-3 mx-auto border-2 border-dashed border-orange-300 ${activeIcons.includes('staff') ? 'ring-4 ring-orange-300' : ''
                      }`}
                  >
                    <Headphones className="h-10 w-10 text-orange-600" />
                  </motion.div>
                  <p className="font-semibold">HA Staff</p>
                  <p className="text-sm text-gray-800 mt-1">If needed</p>
                </motion.div>

                {/* Animated particles */}
                <div className="absolute inset-0 pointer-events-none">
                  <Particle from="resident" to="chatbot" step={animationStep} />
                  <Particle from="chatbot" to="policy" step={animationStep} />
                  <Particle from="policy" to="chatbot" step={animationStep} />
                  <Particle from="chatbot" to="resident" step={animationStep} />
                  <Particle from="resident" to="staff" step={animationStep} />
                  <Particle from="staff" to="resident" step={animationStep} />
                </div>
              </div>
            </motion.div>

            {/* Features Grid */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            >
              <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all flex flex-col h-full">
                <MessageSquare className="h-12 w-12 text-blue-600 mb-4" />
                <h3 className="text-xl font-bold mb-3">Self-Serve Answers</h3>
                <p className="text-gray-600 mb-2">
                  Instant responses to common questions about applications, wait times, and policies.
                </p>
                <p className="text-sm text-blue-600 font-medium mt-auto">
                  Powered by RAG technology for accuracy
                </p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all flex flex-col h-full">
                <Languages className="h-12 w-12 text-pink-600 mb-4" />
                <h3 className="text-xl font-bold mb-3">Auto Language Detection</h3>
                <p className="text-gray-600 mb-2">
                  Detects the resident's language from their first message and switches the UI &amp; responses automatically.
                </p>
                <p className="text-sm text-pink-600 font-medium mt-auto">
                  Seamless across 10+ languages, voice or text
                </p>
              </div>

              <button
                type="button"
                onClick={() => navigate('/translate')}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all text-left cursor-pointer hover:bg-emerald-50 flex flex-col h-full"
              >
                <Library className="h-12 w-12 text-emerald-600 mb-4" />
                <h3 className="text-xl font-bold mb-3">Translation Library</h3>
                <p className="text-gray-600 mb-2">
                  Every public-facing page, form, and document from your authority, translated nightly into 10+ languages with side-by-side originals.
                </p>
                <p className="text-sm text-emerald-600 font-medium inline-flex items-center gap-1 mt-auto">
                  View the library <ArrowRight className="h-3.5 w-3.5" />
                </p>
              </button>

              <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all flex flex-col h-full">
                <Shield className="h-12 w-12 text-purple-600 mb-4" />
                <h3 className="text-xl font-bold mb-3">Policy-Aware AI</h3>
                <p className="text-gray-600 mb-2">
                  Stays current with State &amp; Federal housing policies, discretionary policies {'('}PHA Admin. Plan, ACOP{')'}, and your HA policies.
                </p>
                <p className="text-sm text-purple-600 font-medium mt-auto">
                  Quarterly policy updates included
                </p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all flex flex-col h-full">
                <RefreshCw className="h-12 w-12 text-amber-600 mb-4" />
                <h3 className="text-xl font-bold mb-3">Nightly Content Sync</h3>
                <p className="text-gray-600 mb-2">
                  We crawl your website every night so residents always get today's answer, not last quarter's.
                </p>
                <p className="text-sm text-amber-600 font-medium mt-auto">
                  Notices, events, and policy updates &mdash; always current
                </p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all flex flex-col h-full">
                <Zap className="h-12 w-12 text-violet-600 mb-4" />
                <h3 className="text-xl font-bold mb-3">Fair Housing Compliance</h3>
                <p className="text-gray-600 mb-2">
                  Ensures uniform, compliant responses that advance fair housing goals.
                </p>
                <p className="text-sm text-violet-600 font-medium mt-auto">
                  Built-in bias detection &amp; mitigation
                </p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all flex flex-col h-full">
                <BarChart3 className="h-12 w-12 text-teal-600 mb-4" />
                <h3 className="text-xl font-bold mb-3">HA Manager Dashboard</h3>
                <p className="text-gray-600 mb-2">
                  Track anonymized questions and trends, with automatic flagging of distressing, dangerous, or personal content for staff follow-up.
                </p>
                <p className="text-sm text-teal-600 font-medium mt-auto">
                  Sentiment-triggered escalation &amp; safety analytics
                </p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all flex flex-col h-full">
                <Palette className="h-12 w-12 text-indigo-600 mb-4" />
                <h3 className="text-xl font-bold mb-3">Per-HA Theming</h3>
                <p className="text-gray-600 mb-2">
                  The widget inherits your authority's colors, logo, and hotline number out of the box &mdash; one snippet, your look.
                </p>
                <p className="text-sm text-indigo-600 font-medium mt-auto">
                  Smart escalation to your staff hotline when needed
                </p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all flex flex-col h-full">
                <Accessibility className="h-12 w-12 text-red-600 mb-4" />
                <h3 className="text-xl font-bold mb-3">Universal Accessibility</h3>
                <p className="text-gray-600 mb-2">
                  WCAG 2.2 AA compliant with screen reader support, keyboard navigation, and voice control.
                </p>
                <p className="text-sm text-red-600 font-medium mt-auto">
                  Designed for all abilities
                </p>
              </div>
            </motion.div>

            {/* Security Section */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="mt-16 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-3xl p-12"
            >
              <div className="text-center mb-8">
                <Shield className="h-16 w-16 mx-auto mb-4 text-blue-600" />
                <h3 className="text-3xl font-bold mb-4 text-gray-900">Enterprise-Grade Security</h3>
                <p className="text-xl text-gray-600">Your data & your residents' privacy are our top priority</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="text-center">
                  <CheckCircle className="h-8 w-8 mx-auto mb-2 text-blue-600" />
                  <p className="font-semibold text-gray-900">Encryption</p>
                  <p className="text-sm text-gray-600">In transit & at rest</p>
                </div>
                <div className="text-center">
                  <CheckCircle className="h-8 w-8 mx-auto mb-2 text-blue-600" />
                  <p className="font-semibold text-gray-900">Auto-Purge Data</p>
                  <p className="text-sm text-gray-600">After 90-day</p>
                </div>
                <div className="text-center">
                  <CheckCircle className="h-8 w-8 mx-auto mb-2 text-blue-600" />
                  <p className="font-semibold text-gray-900">SOC 2 Infrastructure</p>
                  <p className="text-sm text-gray-600">Built on SOC 2 compliant hosting</p>
                </div>
                <div className="text-center">
                  <CheckCircle className="h-8 w-8 mx-auto mb-2 text-blue-600" />
                  <p className="font-semibold text-gray-900">WCAG 2.2</p>
                  <p className="text-sm text-gray-600">AA compliance</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default ProductPage;